/**
 * mSTEM generic table primitive (DG-20). Pure presentational — takes
 * columns + data + optional onRowClick + emptyMessage. Used by Wave 2-4
 * manager modals (RobotManagerModal, KitManagerModal, FirmwareWizardModal
 * Step 1).
 *
 * Visual style mirrors LMS `app-data-table`: navy header, blue-tinted
 * shadow, 12px rounded card. See mstem-table.css.
 *
 * Security note (T-02-01-05): cells render via React text children which
 * are auto-escaped. The `render` escape hatch is restricted to functions
 * via PropTypes — no dangerouslySetInnerHTML exposed.
 */
import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './mstem-table.css';

/**
 * Inner row component — bound handlers avoid `react/jsx-no-bind` warnings on
 * the parent map. Each row owns its own click binding to the parent's
 * onRowClick callback.
 */
class MstemTableRow extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['handleClick']);
    }
    handleClick () {
        if (this.props.onRowClick) {
            this.props.onRowClick(this.props.row);
        }
    }
    render () {
        const {row, columns, onRowClick} = this.props;
        const trClassName = classNames(
            styles.mtRow,
            {[styles.mtRowClickable]: !!onRowClick}
        );
        return (
            <tr
                className={trClassName}
                onClick={onRowClick ? this.handleClick : null}
            >
                {columns.map(col => (
                    <td
                        key={col.key}
                        className={styles.mtTd}
                    >
                        {col.render ? col.render(row) : row[col.key]}
                    </td>
                ))}
            </tr>
        );
    }
}

MstemTableRow.propTypes = {
    columns: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
    onRowClick: PropTypes.func,
    row: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
};

const MstemTable = ({columns, data, onRowClick, emptyMessage, className}) => {
    if (!data || data.length === 0) {
        return (
            <div className={classNames(styles.mtCard, className)}>
                <div className={styles.mtEmpty}>{emptyMessage}</div>
            </div>
        );
    }
    return (
        <div className={classNames(styles.mtCard, className)}>
            <div className={styles.mtScrollWrap}>
                <table className={styles.mtTable}>
                    <thead className={styles.mtThead}>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={styles.mtTh}
                                    style={col.width ? {width: col.width} : null}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => (
                            <MstemTableRow
                                key={row.id || idx}
                                columns={columns}
                                row={row}
                                onRowClick={onRowClick}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

MstemTable.propTypes = {
    className: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        width: PropTypes.string,
        render: PropTypes.func
    })).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    emptyMessage: PropTypes.node,
    onRowClick: PropTypes.func
};

MstemTable.defaultProps = {
    emptyMessage: 'No data'
};

export default MstemTable;
